import React from "react";
import { Check, AlertCircle, Info } from "lucide-react";

import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { WorkflowRunListObject } from "@/types/workflowRuns";
import NeedHelpAlert from "@/components/TaskHub/NeedHelpAlert.tsx";

type ApplicationCompletedViewV2Props = {
  application: WorkflowRunListObject;
};

type StatusCellProps = {
  hasNotes: boolean;
  isPremium: boolean;
};

const StatusCell = ({ hasNotes, isPremium }: StatusCellProps) => {
  if (isPremium) {
    return (
      <div className="flex items-center justify-end gap-1">
        <Check className="h-4 w-4 text-green-600" />
        <span className="text-green-600">Generated</span>
      </div>
    );
  }

  if (!isPremium && hasNotes) {
    return (
      <div className="flex items-center justify-end gap-1">
        <AlertCircle className="h-4 w-4 text-red-600" />
        <span className="text-red-600">Failed</span>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-end gap-1">
      {/*<Info className="h-4 w-4 text-gray-400" />*/}
      <span className="text-gray-500 italic">N/A</span>
    </div>
  );
};

const ApplicationCompletedViewV2 = ({ application }: ApplicationCompletedViewV2Props) => {
  const outcomes = application?.workflow_run?.workflow_outcomes || {};

  const processString = (str: string) => {
    if (!str) {return false;}
    const num = Number.parseFloat(str.replaceAll(/[^\d.]/g, ''));
    return Number.isNaN(num) ? false : num;
  };

  return (
    <div className="w-full max-w-3xl mx-auto p-6 space-y-9">
      <Card className="bg-white">
        <CardHeader>
          <div className="flex items-center gap-3 p-3 rounded-md">
            <div className="rounded-full bg-green-100 p-1.5">
              <Check className="h-4 w-4 text-green-600" />
            </div>
            <p className="text-sm font-medium">Application Process Completed</p>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Carrier</TableHead>
                <TableHead className="text-right">Status</TableHead>
                <TableHead className="text-right">Premium</TableHead>
                <TableHead className="text-right w-8"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(outcomes).map(([id, outcome], index) => {
                const isPremium = !!processString((outcome as any).premium_amount);
                const hasNotes = !!(outcome.notes || outcome.error_message);

                return(
                  <TableRow key={index}>
                    <TableCell className="font-medium">{outcome.carrier_name}</TableCell>
                    <TableCell className="text-right">
                      <StatusCell isPremium={isPremium} hasNotes={hasNotes} />
                    </TableCell>
                    <TableCell className="text-right">
                      {isPremium ? (
                        `${outcome.premium_amount?.toLocaleString() || "0"}`
                      ) : (
                        <span className="text-gray-400">--</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {hasNotes && (
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                          </TooltipTrigger>
                          <TooltipContent side="left">
                            <p className="p-1 rounded-md max-w-xs text-sm bg-white border">
                              {outcome.notes || outcome.error_message}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <NeedHelpAlert />
    </div>
  );
};

export default ApplicationCompletedViewV2;
